import { useUserStore } from '~/domains/user';

import { useBookingUrlGenerator, useBookingStore } from '~~/domains/booking';
import { getRedirectUrl as getLoginRedirectUrl } from '~~/domains/ux/helpers/loyaltyBanner';
import { getRawBackendUrl } from '~~/helpers/backend-url.helper';
import { isLaunchedFromRixos } from '~~/helpers/iframe-context.helper';
import { getRedirectType, getRedirectUrl } from '~~/helpers/redirect.helper';
import type { RedirectQueryParameters } from '~~/helpers/redirect.interface';
import { RedirectType } from '~~/helpers/redirect.interface';

function isFrameAllowedRedirect(type: RedirectType | null) {
  return (
    type === RedirectType.Booking ||
    type === RedirectType.BookingRetry ||
    type === RedirectType.ContinueBooking
  );
}

function simplifiedCheckBooking(query: any) {
  return query.dateIn && query.nights && query.ridcode && query.ridcode;
}

function getLocale(to: any) {
  const parts = to.path.split('/');
  return parts[1].length === 2 ? parts[1] : 'en';
}

function checkRixosPaymentCase(to: any, redirectType: RedirectType | null) {
  if (to.query.from_rixos === 'true') {
    const rixos = useRuntimeConfig().public.rixosPartnerLink;
    const locale = getLocale(to);

    if (redirectType === RedirectType.ContinueBooking) {
      return navigateTo(`${rixos}/${locale}/confirmation`, { external: true });
    } else if (redirectType === RedirectType.BookingConfirmationError) {
      return navigateTo(`${rixos}/${locale}/confirmation?error=true`, {
        external: true,
      });
    }
    return false;
  }
  return false;
}

function prepareUrl(url: string, to: any) {
  let result = url;
  if (to.query.origin) {
    result += result.includes('?') ? '&' : '?';
    result += 'origin=' + to.query.origin;
  }

  if (to.query.partner_id) {
    result += result.includes('?') ? '&' : '?';
    result += 'partner_id=' + to.query.partner_id;
  }

  if (to.query.iframe) {
    result += result.includes('?') ? '&' : '?';
    result += 'iframe=' + to.query.iframe;
  }

  return result;
}

function manageLoginStatus(to: any, status: boolean) {
  const userStore = useUserStore();
  const config = useRuntimeConfig();
  const isUserLoginned = userStore.hasValidAccessTokenCookie;

  if (isUserLoginned && !status) {
    userStore.cleanLoginCookie();
  }

  if (!isUserLoginned && status) {
    const loginInitUrl = getLoginRedirectUrl(
      config.public.accorAuthenticationUrl,
      config.public.baseUrl,
      getLocale(to),
      {
        redirect: to.fullPath,
        type: 'login',
      },
    ).toString();

    navigateTo(loginInitUrl, { external: true });
    return true;
  }
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export default defineNuxtRouteMiddleware(async to => {
  const localePath = useLocalePath();
  const serverUrl = getRawBackendUrl();
  const { bookingURL } = useBookingUrlGenerator();
  const bookingStore = useBookingStore();

  if (!to.path.includes('/redirect')) {
    return true;
  }

  if (to.query.currency) {
    bookingStore.setCurrency(to.query.currency as string);
  }

  if ('login' in to.query) {
    const res = await manageLoginStatus(to, to.query.login === 'true');
    if (res) return res;
  }

  const isFromRixos = isLaunchedFromRixos();
  const queryParameters: RedirectQueryParameters = to.query;

  if (isFromRixos && process.server && simplifiedCheckBooking(to.query)) {
    const result = await getRedirectUrl(
      RedirectType.Booking,
      serverUrl,
      localePath,
      bookingURL,
      queryParameters,
    );

    return navigateTo(prepareUrl(result, to));
  }
  const rixos = process.server
    ? ''
    : useRuntimeConfig().public.rixosPartnerLink;

  const redirectType = await getRedirectType(serverUrl, queryParameters);
  if (isFromRixos && !isFrameAllowedRedirect(redirectType)) {
    if (!process.server) {
      parent.postMessage('redirection_error', rixos);
    }
    return true;
  }

  if (checkRixosPaymentCase(to, redirectType)) {
    return true;
  }

  const url: string = await getRedirectUrl(
    redirectType,
    serverUrl,
    localePath,
    bookingURL,
    queryParameters,
  );

  if (url) {
    return navigateTo(localePath(prepareUrl(url, to)));
  }

  // handle error case: for iframe 404, for normal view - homepage
  if (isFromRixos) {
    if (!process.server) {
      parent.postMessage('redirection_error', rixos);
    }
    return true;
  }

  return navigateTo(localePath('/'));
});
